import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { ChatCircleDots } from "@phosphor-icons/react";
import { useCallback, useMemo, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { AButton } from "components/A";
import { permissions, routes } from "config/paths";
import { useAuth, WithAuthorization } from "context";
import { RoundOverrideProvider } from "../../../context/RoundOverrideContext";
import FoundationDataV2Api from "api/issb/foundationDataV2";
import {
  foundationV2Keys,
  useAddFoundationV2CuratedRow,
  useDeleteFoundationV2CuratedRow,
  useFoundationV2CuratedRows,
  useFoundationV2CuratedSaves,
  useFoundationV2CuratedUsage,
  useFoundationV2PendingChanges,
  useFoundationV2Round,
  useFoundationV2Rows,
  useFoundationV2Runs,
  useFoundationV2TargetStructure,
  useFoundationV2Years,
  usePatchFoundationV2CuratedRow,
} from "hooks/query";
import LocalSpinner from "components/LocalSpinner";
import { showErrorToast } from "utils";
import useEsgAdminFeaturesVisible from "hooks/useEsgAdminFeaturesVisible";
import IssbContainer from "../IssbContainer";
import SecondaryHeader from "../components/SecondaryHeader";
import RichTextEditor from "../IssbDataInput/components/RichTextEditor/RichTextEditor";
import AnswerActionButtons from "../IssbDataInput/components/tools/AnswerActionButtons";
import MetricInfoOverlay from "./components/MetricInfoOverlay";
import PendingChangesNotice from "./components/PendingChangesNotice";
import { pendingDatapointUids } from "utils/foundationPendingChanges";
import useAirUserRoles from "utils/AuthUtils";
import EmptyState from "./components/EmptyState";
import FoundationDataTable from "./components/FoundationDataTable";
import ManageChangedDataModal from "./components/ManageChangedDataModal";
import useAnswerImpactGate from "./components/useAnswerImpactGate";
import PriorYearCards from "./components/PriorYearCards";
import SourcesPanel from "./components/SourcesPanel";
import TargetStructurePanel from "./components/TargetStructurePanel";
import useFoundationYearQuestion from "./components/useFoundationYearQuestion";
import useSourceExtraction from "./components/useSourceExtraction";
import useYearAnswerSave from "./components/useYearAnswerSave";
import FoundationWorkstreamDetails from "./components/FoundationWorkstreamDetails";
import { useRightSidebar } from "../../../context/RightSidebarContext";

/**
 * One workstream of one reporting year: its sources, the figures approved from them, and its notes.
 *
 * Remounted on `?id=` so every piece of local state resets — the route does not change when the question does,
 * and useEffect resets show the previous workstream's sources for a frame.
 */
function FoundationYearDetailPage() {
  const [searchParams] = useSearchParams();
  const questionId = searchParams.get("id");
  return <FoundationYearDetailPageInner key={questionId} questionId={questionId} />;
}

function FoundationYearDetailPageInner({ questionId }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { userPermissions, callApi } = useAuth();
  // Toggle-aware: false for non-admins AND for an ESG Admin who has hidden admin features in the header.
  const showAdminFeatures = useEsgAdminFeaturesVisible();
  const navigate = useNavigate();
  // Whether the side tray (Manage, AI, a citation) is showing — the layout reserves its width only then.
  const { isOpen: isSidebarOpen } = useRightSidebar();
  const [usageFor, setUsageFor] = useState(null);
  /*
    Inline editors currently open in the figures table (editorId -> true). Submit/Approve are blocked while
    any is open: the change in it is neither saved nor discarded, so approving now would publish a set that
    is not the one on screen. Stable callback — every editor's effect depends on it.
  */
  const [openEditors, setOpenEditors] = useState({});
  const onEditingChange = useCallback((id, isOpen) => {
    setOpenEditors((prev) => {
      if (!!prev[id] === isOpen) return prev;
      const next = { ...prev };
      if (isOpen) next[id] = true;
      else delete next[id];
      return next;
    });
  }, []);
  const hasOpenEditor = Object.keys(openEditors).length > 0;

  const onError = (err) =>
    showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);

  const { data: roundStatus } = useFoundationV2Round({ onError });
  const round = roundStatus?.round;

  /*
    The workstream row this question belongs to. `year` alone would not name it — a year holds one row per
    workstream — so every figure and run endpoint is keyed on this id, resolved from the question.
  */
  const { data: years } = useFoundationV2Years({ onError });
  const dataYear = useMemo(
    () => (years ?? []).find((y) => y.questionId === questionId) ?? null,
    [years, questionId]
  );

  // The financial-year link is for whoever can change it (the setting is ORG_ADMIN).
  const { isOrgAdmin } = useAirUserRoles();
  // Entity and reporting period live in the Manage tray on this page, not in the page header.
  const trayDetails = useMemo(
    () => <FoundationWorkstreamDetails dataYear={dataYear} canEditFinancialYear={isOrgAdmin} />,
    [dataYear, isOrgAdmin]
  );
  const { question, canEdit } = useFoundationYearQuestion(questionId, trayDetails);
  const dataYearId = dataYear?.id ?? null;
  const year = dataYear?.year ?? null;

  const { data: runs } = useFoundationV2Runs(dataYearId, { onError });
  const { data: rows } = useFoundationV2Rows(dataYearId, { onError });
  const { data: curatedRows } = useFoundationV2CuratedRows(dataYearId, { onError });
  const { data: curatedSaves } = useFoundationV2CuratedSaves(dataYearId, { onError });
  const { data: curatedUsage } = useFoundationV2CuratedUsage(dataYearId, { onError });
  const { data: pendingChanges } = useFoundationV2PendingChanges(dataYearId, { onError });
  /* Per workstream, not per organisation: the datapoints wanted for targets are not those wanted for
     emissions, and the table renders a gap row for every one it holds no figure for. */
  const { data: targetStructure } = useFoundationV2TargetStructure(dataYear?.questionGroup, { onError });

  const runList = useMemo(() => runs ?? [], [runs]);
  const rowList = useMemo(() => rows ?? [], [rows]);
  const curatedList = useMemo(() => curatedRows ?? [], [curatedRows]);
  const usageByDatapointUid = useMemo(
    () => Object.fromEntries((curatedUsage ?? []).map((u) => [u.datapointUid, u])),
    [curatedUsage]
  );

  const {
    sources,
    persistedSourceIds,
    persistSources,
    answerHtml,
    setAnswerDraft,
    answerModifiedFlag,
    handleSave,
    isSaving,
  } = useYearAnswerSave(question, questionId, runList);

  const invalidateRows = () => {
    queryClient.invalidateQueries(foundationV2Keys.rows(dataYearId));
    queryClient.invalidateQueries(foundationV2Keys.years());
  };
  const invalidateCurated = () => {
    queryClient.invalidateQueries(foundationV2Keys.curated(dataYearId));
    queryClient.invalidateQueries(foundationV2Keys.pending(dataYearId));
    queryClient.invalidateQueries(foundationV2Keys.curatedSaves(dataYearId));
    queryClient.invalidateQueries(foundationV2Keys.years());
  };

  const extraction = useSourceExtraction({
    dataYearId,
    year,
    runs: runList,
    onRunsChanged: invalidateRows,
  });

  /*
    A patch archives the row and writes the next version, so an id from before the last edit is superseded and
    the server answers 409. Mapped here because the shared handler would surface axios's "Request failed with
    status code 409", which tells the user nothing about what to do.
  */
  const patchRow = usePatchFoundationV2CuratedRow({
    onSuccess: invalidateCurated,
    onError: (err) =>
      err.response?.status === 409
        ? showErrorToast(
            toast,
            // Superseded row, or (ESG-4012) a workstream past the editing stages — the server says which.
            err.response?.data?.parameters?.message ??
              "That figure changed while you were editing it. The table has been refreshed — try again."
          ) || invalidateCurated()
        : onError(err),
  });
  const deleteRow = useDeleteFoundationV2CuratedRow({ onSuccess: invalidateCurated, onError });
  const addRow = useAddFoundationV2CuratedRow({ onSuccess: invalidateCurated, onError });

  /*
    ESG-4012: the "Manage changed data" pre-flight runs on the owner's APPROVAL, not on each edit. An edit only
    changes the working set; answers and AI Review keep reading the last approved figures until the owner
    approves, so that is the moment a change can strand an answer — and the moment to ask what to do about it.

    The pending list is re-fetched at the moment of approval rather than read from the cache: the uids asked
    about must be the ones this approval is about to publish.
  */
  const impactGate = useAnswerImpactGate();
  const beforeAction = (type, proceed) => {
    if (type !== "APPROVE") {
      proceed().catch(() => {});
      return;
    }
    queryClient
      .fetchQuery(foundationV2Keys.pending(dataYearId), () =>
        callApi(FoundationDataV2Api.listPendingChanges, dataYearId)
      )
      .then(
        (pending) => impactGate.gate(pendingDatapointUids(pending), proceed),
        // Fail open, like the gate itself: a pre-flight that could not be fetched must not block an approval.
        () => impactGate.gate([], proceed)
      );
  };
  const onApproved = () => {
    // An approval publishes, so everything a consumer reads — and every pending marker — has moved.
    invalidateCurated();
    queryClient.invalidateQueries(foundationV2Keys.curatedUsage(dataYearId));
    queryClient.invalidateQueries(foundationV2Keys.curatedForYear());
  };

  /*
    Checked against the CHAT route's own permission rather than this page's: the two lists agree today, and
    gating the button on the page's roles would let them drift into a button that navigates somewhere the user
    is refused.
  */
  const canChat = useMemo(
    () => !!userPermissions?.some((role) => permissions.foundationDataV2Chat.includes(role)),
    [userPermissions]
  );
  /*
    Scoped to this WORKSTREAM, not to the year: a chat session is bound to the row its figures belong to, and a
    year now holds three. Passing the year alone would open a session over whichever workstream a year lookup
    happened to return.
  */
  const openChat = () =>
    navigate(`${routes.foundationDataV2Chat}?dataYearId=${dataYearId}`);

  const priorYears = useMemo(
    () =>
      (years ?? [])
        // Same workstream AND same entity: another subsidiary's 2024 is not this one's prior year.
        .filter(
          (y) =>
            y.questionGroup === dataYear?.questionGroup &&
            y.entityId === dataYear?.entityId &&
            y.questionId !== questionId
        )
        .sort((a, b) => b.year - a.year)
        .map((y) => ({
          id: y.questionId,
          question: `${y.questionGroup} ${y.year}`,
          stage: y.stageName,
          stageType: y.stageType,
        })),
    [years, dataYear, questionId]
  );

  if (!question || !dataYear) {
    return (
      <SecondaryHeader showRoundSelector={false} showActions={false}>
        <LocalSpinner />
      </SecondaryHeader>
    );
  }

  return (
    /* RoundOverrideProvider so useAirUserRoles resolves against the v2 round rather than the navbar's
       selectedRound — the v2 round is filtered out of every selector, so it can never BE the selected one. */
    <RoundOverrideProvider round={round}>
      <SecondaryHeader showRoundSelector={false} showActions={false}>
        {/* The right sidebar floats over content, so its width has to be reserved here. `minmax(0, 1fr)`
            rather than `1fr`: a grid item's default `min-width: auto` lets its content force the track wider
            than its share, and the eight-column metrics table is wide enough to do it — which ran the column
            under the sidebar and stopped the table's own overflowX from ever engaging, because overflow needs
            a bounded width to overflow against. */}
        {/* Bounded height + own scroll so the sticky footer below has a scroll container to stick to.
            SecondaryHeader's content box is overflowY="auto" but unbounded, so it never scrolls itself
            and would otherwise capture (and neutralise) position: sticky. 52px/71px = SecondaryHeader
            bar + navbar, the same offsets SecondaryHeader uses for its own side panel. */}
        {/* Full width, as the data task page: the side tray starts closed here, so its 400px is reserved only
            while it is open, and the content is not capped — the table uses the screen. */}
        <Grid
          templateColumns={isSidebarOpen ? "minmax(0, 1fr) 400px" : "minmax(0, 1fr)"}
          h="calc(100vh - 52px - 71px)"
          overflowY="auto"
        >
          <IssbContainer margin="0">
            <Box w="100%" px="10%" py={5}>
              <Flex direction="column" gap={4}>
                <Flex align="center">
                  <AButton
                    shouldConfirm={answerModifiedFlag}
                    confirmConfig={{
                      title: "Unsaved changes?",
                      message:
                        "It looks like you are navigating away with unsaved changes. Continuing will mean these changes will be lost.",
                      confirmText: "Discard Changes",
                      cancelText: "Cancel",
                    }}
                    leftIcon={<FaArrowLeft />}
                    variant="link"
                    color="limeShade.600"
                    href={
                      dataYear.entityId
                        ? `${routes.foundationDataV2}?entity=${dataYear.entityId}`
                        : routes.foundationDataV2
                    }
                  >
                    <Text as="span" color="whiteShade.600">
                      Foundation Data
                    </Text>
                  </AButton>
                </Flex>

                <Box>
                  {/* Entity and reporting period (with Financial year settings) are in the Manage tray. */}
                  <Heading fontSize="heading.l" color="darkTeal" mb={4}>
                    {question.question}
                  </Heading>
                  <Text fontSize="body.s" color="gray.600">
                    {question.description}
                  </Text>
                </Box>

                <SourcesPanel
                  dataYearId={dataYearId}
                  dataYear={dataYear}
                  round={round}
                  canEdit={canEdit}
                  sources={sources}
                  persistedSourceIds={persistedSourceIds}
                  onSourcesChange={persistSources}
                  runs={runList}
                  rows={rowList}
                  targetStructure={targetStructure}
                  currentRows={curatedList}
                  extraction={extraction}
                  onAccepted={invalidateCurated}
                  onChat={openChat}
                />

                {/* Collapsed: it governs how the table below groups, so it belongs above it — but it is
                    configuration, not this year's work, and open by default it competes with the sources.
                    ESG_ADMIN-only: it's org-wide steering configuration (which datapoints are collected
                    at all, not this year's figures), so an org's own ORG_ADMIN/ORG_MEMBER assignees should
                    not see or edit it here. */}
                {showAdminFeatures && (
                  <Accordion allowToggle>
                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md">
                      <AccordionButton py={4} px={4}>
                        <Box flex="1" textAlign="left" fontWeight="600" color="darkTeal" fontSize="sm">
                          Target structure
                          {targetStructure ? ` · ${targetStructure.datapointCount} datapoints` : ""}
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel>
                        <TargetStructurePanel
                          structure={targetStructure}
                          canEdit={canEdit}
                          questionGroup={dataYear.questionGroup}
                          showChrome={false}
                        />
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}

                <Flex justify="space-between" align="flex-end" gap={4} wrap="wrap">
                  <Flex direction="column" gap={1} minW={0} />

                  {/* Hidden rather than disabled without the chat role, and hidden with no extracted rows,
                      because a session over an empty workstream has nothing to talk about. */}
                  {/* canEdit too (ESG-4012): the chat saves figures, and the server refuses a save past the
                      editing stages — a button leading to a session whose save must fail is worse than none. */}
                  {showAdminFeatures && canChat && canEdit && rowList.length > 0 ? (
                    <Tooltip
                      label="Clean up these metrics by chatting about them — normalise units, tidy site names, group them. Saves to the applied set; the extracted rows are never changed."
                      openDelay={300}
                    >
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<ChatCircleDots size={16} />}
                        flexShrink={0}
                        onClick={openChat}
                      >
                        Chat with data
                      </Button>
                    </Tooltip>
                  ) : null}
                </Flex>

                <PendingChangesNotice pending={pendingChanges} />

                {curatedList.length === 0 ? (
                  <EmptyState
                    title="No data points yet"
                    description={
                      canEdit
                        ? "Review a source above to add its figures, or tidy one first in Chat with data."
                        : "An owner or assignee needs to review a source, or tidy one in Chat with data."
                    }
                  />
                ) : (
                  <FoundationDataTable
                    rows={curatedList}
                    canEdit={canEdit}
                    structure={targetStructure}
                    usageByDatapointUid={usageByDatapointUid}
                    onShowUsage={setUsageFor}
                    onSaveRow={(params, done) =>
                      patchRow.mutate(params, { onSuccess: () => done?.() })
                    }
                    onDeleteRow={(id) => deleteRow.mutate(id)}
                    onAddRow={(row, reset) =>
                      addRow.mutate({ dataYearId, row }, { onSuccess: () => reset?.() })
                    }
                    savingRowId={patchRow.isLoading ? patchRow.variables?.id : null}
                    deletingRowId={deleteRow.isLoading ? deleteRow.variables : null}
                    isAdding={addRow.isLoading}
                    onEditingChange={onEditingChange}
                  />
                )}

                <Box>
                  <Text fontSize="sm" fontWeight="600" color="gray.600" mb={2}>
                    Additional Information
                  </Text>
                  <RichTextEditor
                    content={answerHtml}
                    onChange={(html) => setAnswerDraft(html)}
                    isDisabled={!canEdit}
                  />
                </Box>

                {/* Above the sticky footer, not below it: a sticky `bottom="0"` element stays pinned to
                    the bottom of ITS CONTAINING BLOCK, not just until its next sibling — so anything
                    placed after it in the DOM would scroll UNDER it rather than past it. Moved here so the
                    footer below is genuinely the last thing in the scroll flow, same as the data-entry
                    task page's own footer (OrganizationQuestionnaire.jsx), which has nothing after it
                    either. */}
                <PriorYearCards questions={priorYears} answerModifiedFlag={answerModifiedFlag} />

                {/* Sticky footer, same styling as the data-entry task page's (OrganizationQuestionnaire.jsx
                    `data-sticky-footer`): one row of equal-width buttons. AnswerActionButtons is shared and
                    stacks its buttons in a column with mt=8; `display: contents` on its wrapper and root
                    lifts those buttons into this row instead, without editing the shared component. */}
                <Flex
                  data-sticky-footer
                  gap={3}
                  position="sticky"
                  bottom="0"
                  bg="white"
                  borderTop="1px solid"
                  borderColor="gray.200"
                  zIndex="10"
                  py={4}
                  mt={2}
                  sx={{ "& button": { mt: 0, flex: 1, w: "auto" } }}
                >
                  {canEdit ? (
                    <Button
                      variant="primary"
                      isLoading={isSaving}
                      isDisabled={!answerModifiedFlag}
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                  ) : null}
                  <Box display="contents" sx={{ "& > div": { display: "contents" } }}>
                    <AnswerActionButtons
                      questionData={question}
                      questionId={question.id}
                      currentStageType={question?.stageType}
                      currentStageName={question?.stage}
                      /* An open inline editor counts as an unsaved edit here, so Submit/Approve disable
                         ("The question has unsaved edits") until it is saved or cancelled. */
                      answerModifiedFlag={answerModifiedFlag || hasOpenEditor}
                      /* The figures ARE the content (ESG-4021): `answer` is only the optional notes, so the shared
                         "has an answer or a file" test would leave a workstream full of figures unsubmittable.
                         The working set, not the approved one — submitting is what asks for it to be approved. */
                      hasContent={curatedList.length > 0}
                      beforeAction={beforeAction}
                      onActionDone={onApproved}
                    />
                  </Box>
                </Flex>
              </Flex>
            </Box>
          </IssbContainer>

          <ManageChangedDataModal {...impactGate.modal} />

          <MetricInfoOverlay
            isOpen={!!usageFor}
            onClose={() => setUsageFor(null)}
            row={usageFor}
            dataYear={dataYear}
          />
        </Grid>
      </SecondaryHeader>
    </RoundOverrideProvider>
  );
}

export default WithAuthorization(FoundationYearDetailPage);
